/** Matrix report helpers — isolated from summary/tabular report logic. */

import { logReportCurrency } from '../debug/reportCurrencyDebug';

export const GROUP_AXIS_ROW = 'row';
export const GROUP_AXIS_COLUMN = 'column';

export function isMatrixMode(columnGroups) {
  return Array.isArray(columnGroups) && columnGroups.length > 0;
}

export function resolveReportType(selectedGroups, selectedColumnGroups, fallback = 'tabular') {
  if (isMatrixMode(selectedColumnGroups)) return 'matrix';
  if (Array.isArray(selectedGroups) && selectedGroups.length > 0) return 'summary';
  return fallback;
}

export function splitLoadedReportFields(fields, convertAggregationValue) {
  const rowGroups = [];
  const columnGroups = [];
  const selectedFieldsList = [];
  const summarized = {};

  (fields || []).forEach(field => {
    const axis = String(field.group_axis || GROUP_AXIS_ROW).toLowerCase();
    if (field.is_grouped) {
      const entry = {
        ...field,
        id: field.field_config_id || field.id,
        summaryType: 'none',
      };
      if (axis === GROUP_AXIS_COLUMN) columnGroups.push(entry);
      else rowGroups.push(entry);
      // Keep grouped fields in column layout; preview omits duplicates in detail columns.
      selectedFieldsList.push(entry);
    } else {
      const entry = {
        ...field,
        id: field.field_config_id || field.id,
        summaryType:
          field.is_aggregated && field.aggregate_function && convertAggregationValue
            ? convertAggregationValue(field.aggregate_function)
            : 'none',
      };
      selectedFieldsList.push(entry);
      if (field.is_aggregated && field.aggregate_function && convertAggregationValue) {
        summarized[entry.id] = {
          aggregation: convertAggregationValue(field.aggregate_function),
        };
      }
    }
  });

  return { rowGroups, columnGroups, selectedFieldsList, summarized };
}

export function normalizeReportAggregation(aggregation) {
  if (aggregation == null || aggregation === '') return '';
  const raw = String(aggregation).trim().toLowerCase();
  const aliases = { average: 'avg', mean: 'avg' };
  return aliases[raw] || raw;
}

export function resolveSummarizedFieldInfo(field, summarizedFields) {
  if (!field || !summarizedFields) return null;
  const direct =
    summarizedFields[field.id] ||
    summarizedFields[field.field_config_id] ||
    summarizedFields[field.field_id];
  if (direct) return direct;
  const fieldName = field.field_name;
  const matchKey = Object.keys(summarizedFields).find((key) => {
    const entry = summarizedFields[key];
    return (
      String(key) === String(field.id) ||
      String(key) === String(field.field_config_id) ||
      String(key) === String(field.field_id) ||
      entry?.field?.id === field.id ||
      entry?.field_name === fieldName
    );
  });
  return matchKey ? summarizedFields[matchKey] : null;
}

/** Report formula columns (growth %, win rate) — not object calculated fields. */
function isReportFormulaColumn(field) {
  return Boolean(field?.is_formula && field?.formula_key);
}

/** Non-grouped selected fields that have a summarize aggregation (for chart metadata). */
export function collectSummarizedMeasureFields(
  selectedFields = [],
  summarizedFields = {},
  groupedFields = []
) {
  const groupKey = (f) =>
    `${f.object_name || 'main'}|${f.field_name}`;
  const groupedKeys = new Set((groupedFields || []).map(groupKey));

  const measures = [];
  const seenFieldNames = new Set();

  (selectedFields || []).forEach((field) => {
    if (!field?.field_name || isReportFormulaColumn(field)) return;
    if (groupedKeys.has(groupKey(field))) return;

    const info = resolveSummarizedFieldInfo(field, summarizedFields);
    const aggregation = normalizeReportAggregation(info?.aggregation);
    if (!aggregation || aggregation === 'none') return;

    if (seenFieldNames.has(field.field_name)) return;
    seenFieldNames.add(field.field_name);

    const dt = String(field.data_type || '').toLowerCase();
    const measure = {
      ...field,
      aggregation,
    };
    if (dt === 'calculated') {
      const displayConfig = field.display_config || field.displayConfig;
      let calculatedReturnType = null;
      try {
        const parsed =
          typeof displayConfig === 'string'
            ? JSON.parse(displayConfig)
            : displayConfig;
        calculatedReturnType =
          parsed?.calculated?.returnType ||
          parsed?.calculated?.return_type ||
          parsed?.returnType ||
          parsed?.return_type ||
          null;
      } catch {
        calculatedReturnType = null;
      }
      if (calculatedReturnType) {
        measure.calculatedReturnType = String(calculatedReturnType).toLowerCase();
      }
    }
    measures.push(measure);
  });

  return measures;
}

export function buildMeasuresFromSummarized(selectedFields, summarizedFields) {
  const measures = [];
  (selectedFields || []).forEach(field => {
    const info = resolveSummarizedFieldInfo(field, summarizedFields);
    const aggregation = normalizeReportAggregation(info?.aggregation);
    if (aggregation) {
      measures.push({
        ...field,
        id: field.id || field.field_config_id || field.field_id,
        aggregation,
        data_type: field.data_type,
        display_config: field.display_config || field.displayConfig,
        displayConfig: field.display_config || field.displayConfig,
        scale: field.scale,
      });
    }
  });

  logReportCurrency('buildMeasuresFromSummarized', {
    inputFieldCount: (selectedFields || []).length,
    summarizedKeys: Object.keys(summarizedFields || {}),
    measureCount: measures.length,
    measures: measures.map(m => ({
      id: m.id,
      field_name: m.field_name,
      data_type: m.data_type,
      aggregation: m.aggregation,
    })),
  });

  return measures;
}

export function fieldGroupAxis(fieldId, selectedGroups, selectedColumnGroups) {
  if (selectedColumnGroups.some(g => g.id === fieldId)) return GROUP_AXIS_COLUMN;
  if (selectedGroups.some(g => g.id === fieldId)) return GROUP_AXIS_ROW;
  return null;
}
